import ApiClient from '../../core/ApiClient';

function apiClient() {
    return new ApiClient();
}

/** Same in-flight GET pages?type=blog shared by all HomeGuidesSection mounts; skip repeat when cached for lang. */
let blogsFetchInflight = null;
let blogsLoadedLangKey = null;

/** Same idea for the blog_category filter list (BlogCategoryFilters). */
let categoriesFetchInflight = null;
let categoriesLoadedLangKey = null;

function blogsLangKey(rootState) {
    const id = rootState?.lang?.id;
    return id != null && id !== '' ? String(id) : '';
}

function readCategoryPostCount(category) {
    const total = category?.post_count ?? category?.total ?? category?.posts_count ?? category?.count ?? null;

    if (total === null || total === '') {
        return 0;
    }

    const count = Number(total);

    return Number.isFinite(count) ? Math.max(0, count) : 0;
}

async function fetchCategoryPostCount(categoryId) {
    const id = Number(categoryId);

    if (!Number.isFinite(id) || id <= 0) {
        return 0;
    }

    try {
        const response = await apiClient().request({
            path: 'pages?type=blog',
            method: 'GET',
            data: {
                type: 'blog',
                page: 1,
                perPage: 1,
                category_ids: [id],
            },
        });

        return Math.max(0, Number(response?.data?.pagination?.total ?? 0));
    } catch (e) {
        return 0;
    }
}

export const pages = {
    namespaced: true,

    state: {
        blogs: [],
        loading: false,
        categories: [],
        categoriesLoading: false
    },

    getters: {
        guideBlogs: (state) => {
            const blogs = Array.isArray(state.blogs) ? state.blogs : [];

            return blogs
                .filter(item => String(item?.parent?.value || '') === 'guide')
                .slice(0, 3);
        },

        blogBlogs: (state) => {
            const blogs = Array.isArray(state.blogs) ? state.blogs : [];

            return blogs
                .filter(item => {
                    const parentValue = String(item?.parent?.value || '');

                    return ['blog', 'news'].includes(parentValue);
                })
                .slice(0, 3);
        },

        blogCategories: (state) => {
            const categories = Array.isArray(state.categories) ? state.categories : [];

            return categories.filter((category) => readCategoryPostCount(category) > 0);
        }
    },

    mutations: {
        SET_LOADING(state, loading) {
            state.loading = !!loading;
        },
        SET_BLOGS(state, blogs) {
            state.blogs = blogs;
        },
        SET_CATEGORIES_LOADING(state, loading) {
            state.categoriesLoading = !!loading;
        },
        SET_CATEGORIES(state, categories) {
            state.categories = Array.isArray(categories) ? categories : [];
        }
    },

    actions: {
        /**
         * @param {{ force?: boolean }} [options]
         */
        async fetchBlogs({ commit, state, rootState }, options = {}) {
            const force = options?.force === true;
            const key = blogsLangKey(rootState);
            const hasRows = Array.isArray(state.blogs) && state.blogs.length > 0;

            if (!force && hasRows && blogsLoadedLangKey === key) {
                return;
            }

            if (blogsFetchInflight) {
                return blogsFetchInflight;
            }

            blogsFetchInflight = (async () => {
                commit('SET_LOADING', true);
                try {
                    const response = await apiClient().request({
                        path: 'pages?type=blog',
                        method: 'GET',
                        data: {
                            type: 'blog'
                        }
                    });

                    const blogs = response?.data?.data || [];

                    commit('SET_BLOGS', blogs);
                    blogsLoadedLangKey = key;
                } catch (e) {
                    commit('SET_BLOGS', []);
                    blogsLoadedLangKey = null;
                } finally {
                    commit('SET_LOADING', false);
                    blogsFetchInflight = null;
                }
            })();

            return blogsFetchInflight;
        },

        /**
         * Blog category list used to build the filter pills.
         * GET pages?type=blog_category&page=1
         *
         * @param {{ force?: boolean }} [options]
         */
        async fetchCategories({ commit, state, rootState }, options = {}) {
            const force = options?.force === true;
            const key = blogsLangKey(rootState);
            const hasRows = Array.isArray(state.categories) && state.categories.length > 0;

            if (!force && hasRows && categoriesLoadedLangKey === key) {
                return;
            }

            if (categoriesFetchInflight) {
                return categoriesFetchInflight;
            }

            categoriesFetchInflight = (async () => {
                commit('SET_CATEGORIES_LOADING', true);
                try {
                    const response = await apiClient().request({
                        path: 'pages?type=blog_category&page=1',
                        method: 'GET',
                        data: {
                            type: 'blog_category',
                            page: 1
                        }
                    });

                    const categories = response?.data?.data || [];
                    const enriched = await Promise.all(
                        categories.map(async (category) => {
                            const categoryId = Number(category?.id);
                            const postCount = Number.isFinite(categoryId) && categoryId > 0
                                ? await fetchCategoryPostCount(categoryId)
                                : 0;

                            return {
                                ...category,
                                post_count: postCount,
                            };
                        })
                    );

                    commit('SET_CATEGORIES', enriched);
                    categoriesLoadedLangKey = key;
                } catch (e) {
                    commit('SET_CATEGORIES', []);
                    categoriesLoadedLangKey = null;
                } finally {
                    commit('SET_CATEGORIES_LOADING', false);
                    categoriesFetchInflight = null;
                }
            })();

            return categoriesFetchInflight;
        }
    }
};
